import React, { useMemo } from 'react'
// components
import { Badge, Tooltip, OverlayTrigger } from 'react-bootstrap'

// definitions
import * as AppDefinitions from '../../../App.d'
import { StripeInvoice } from '../../../../../back-end/utilities/apiDefinitions'
import { HistoryView, InvoiceRetryBody } from '../../../screens/subsync/subsync'
import { Listing, ListingColumn } from '../../ui/listing/listing'

// utilities
import { date, time, properCase, currency } from '../../../utilities/format'

// helper functions
import * as functions from './functions'

interface InvoiceHistoryProps {
	appState: AppDefinitions.AppState
	stripeFeesBillsEnabled: boolean
	creditNotesEnabled: boolean
	historyView: HistoryView
	updateHistoryView: (historyView: HistoryView) => void
	retryFailedInvoiceTransaction: (body: InvoiceRetryBody) => void
}

const InvoiceHistory = (props: InvoiceHistoryProps) => {
	const columns = useMemo(() => getColumns(props.stripeFeesBillsEnabled), [props.stripeFeesBillsEnabled])
	return (
		<Listing<StripeInvoice>
			name="Event"
			namePlural="Events"
			list={props.historyView.events}
			getIDFunc={(item) => item.stripeInvoiceID!}
			innerTableClassName="subsync-table"
			individualActions={(item) => functions.getInvoiceOverflowMenuOptions(item, props)}
			columns={columns}
			defaultSort={{ column: 'createTs', order: 'DSC' }}
			isLoading={false}
			paginationControls="bottom"
		/>
	)
}

const getColumns = (billsEnabled: boolean) =>
	(
		[
			{
				value: (item) => item.events![0].createTs!,
				render: (item) => <>{`${date(item.events![0].createTs!)} ${time(item.events![0].createTs!)}`}</>,
				showHeader: true,
				headerText: 'Time',
				sortColumnName: 'createTs',
				filterType: 'dateRange',
				filterOptions: {
					columnName: 'createTs',
				},
			},
			{
				value: (item) => functions.getOriginalInvoiceEvent(item.events)?.stripeInvoiceNumber,
				render: (item) => <p>{functions.getOriginalInvoiceEvent(item.events)?.stripeInvoiceNumber}</p>,
				showHeader: true,
				headerText: 'Invoice',
				sortColumnName: 'stripeInvoice.number',
				filterType: 'string',
				filterOptions: {
					columnName: 'stripeInvoice.number',
				},
			},
			{
				value: (item) => item.invoicee!,
				render: (item) => <p>{item.invoicee!}</p>,
				showHeader: true,
				headerText: 'Contact',
				sortColumnName: 'invoicee',
				filterType: 'string',
				filterOptions: {
					columnName: 'invoicee',
				},
			},
			{
				value: (item) => item.events![0].type!,
				render: (item) => <p>{properCase(item.events![0].type!.split('.')[0])}</p>,
				showHeader: true,
				headerText: 'Action',
				sortColumnName: 'type',
				filterType: 'dropdown',
				filterOptions: {
					columnName: 'type',
					options: [
						{ value: 'created', label: 'Created' },
						{ value: 'credit', label: 'Credit' },
						{ value: 'paid', label: 'Paid' },
						{ value: 'updated', label: 'Updated' },
						{ value: 'voided', label: 'Voided' },
					],
				},
			},
			{
				value: (item) => functions.getOriginalInvoiceEvent(item.events)?.stripeInvoiceAmount?.toString(),
				render: (item) => {
					const originalEvent = functions.getOriginalInvoiceEvent(item.events)
					return originalEvent ? (
						originalEvent.result !== 'success' ? (
							<OverlayTrigger
								placement={'left'}
								overlay={<Tooltip id={`${originalEvent.subSyncEventID}-tooltip`}>{originalEvent.message}</Tooltip>}
							>
								<Badge variant={originalEvent.result === 'error' ? 'danger' : 'warning'} className={'subsync-badge'}>
									{currency(originalEvent.stripeInvoiceAmount, originalEvent.stripeInvoiceCurrency)}
								</Badge>
							</OverlayTrigger>
						) : (
							<Badge variant={'success'} className={'subsync-badge'}>
								{currency(originalEvent.stripeInvoiceAmount, originalEvent.stripeInvoiceCurrency)}
							</Badge>
						)
					) : (
						<></>
					)
				},
				showHeader: true,
				headerText: 'Amount',
				sortColumnName: 'stripeInvoiceAmount',
				filterType: 'string',
				filterOptions: {
					columnName: 'stripeInvoiceAmount',
				},
			},
			{
				value: (item) => item.stripeOrg!.name!,
				render: (item) => <p>{item.stripeOrg!.name!}</p>,
				showHeader: true,
				headerText: 'Stripe',
				sortColumnName: 'stripeOrg.name',
				filterType: 'string',
				filterOptions: {
					columnName: 'stripeOrg.name',
				},
			},
			{
				value: (item) => item.events![0].xeroOrg?.name || '',
				render: (item) => <p>{item.events![0].xeroOrg?.name}</p>,
				showHeader: true,
				headerText: 'Xero',
				sortColumnName: 'xeroOrg.name',
				filterType: 'string',
				filterOptions: {
					columnName: 'xeroOrg.name',
				},
			},
			{
				value: (item) => functions.getMostRecentRelevantEvent(item.events)?.result?.toString(),
				render: (item) => {
					const latestEvent = functions.getMostRecentRelevantEvent(item.events)
					return latestEvent ? (
						latestEvent.result === 'error' ? (
							<OverlayTrigger placement={'left'} overlay={<Tooltip id={`${latestEvent.subSyncEventID}-tooltip`}>{latestEvent.message}</Tooltip>}>
								<Badge variant={'danger'} className={'subsync-badge'}>
									Error
								</Badge>
							</OverlayTrigger>
						) : (
							<Badge
								variant={latestEvent.result === 'holding' || latestEvent.result === 'queued' ? 'warning' : 'success'}
								className={'subsync-badge'}
								style={{ textTransform: 'capitalize' }}
							>
								{latestEvent.result}
							</Badge>
						)
					) : (
						<></>
					)
				},
				showHeader: true,
				headerText: 'Processing Status',
				sortColumnName: 'result',
				filterType: 'string',
				filterOptions: {
					columnName: 'result',
				},
			},
			{
				value: (item) => functions.getLatestInvoiceFinalisedTs(item.events)?.invoiceFinalisedTs?.toString() || '',
				render: (item) => {
					const originalEvent = functions.getLatestInvoiceFinalisedTs(item.events)
					return originalEvent && originalEvent.invoiceFinalisedTs
						? `${date(originalEvent.invoiceFinalisedTs)} ${time(originalEvent.invoiceFinalisedTs)}`
						: ''
				},
				showHeader: true,
				headerText: 'Finalised Date',
				sortColumnName: 'invoiceFinalisedTs',
				filterType: 'dateRange',
				filterOptions: {
					columnName: 'invoiceFinalisedTs',
				},
			},
		] as ListingColumn<StripeInvoice>[]
	).concat(
		billsEnabled
			? [
					{
						value: (item) => (functions.getPaidInvoiceEvent(item.events)?.stripeInvoiceAmount || '').toString(),
						render: (item) => {
							const paidEvent = functions.getPaidInvoiceEvent(item.events)
							return paidEvent ? (
								paidEvent.result !== 'success' ? (
									<OverlayTrigger
										placement={'left'}
										overlay={<Tooltip id={`${paidEvent.subSyncEventID}-fees-tooltip`}>{paidEvent.message}</Tooltip>}
									>
										<Badge variant={paidEvent.result === 'error' ? 'danger' : 'warning'} className={'subsync-badge'}>
											{currency(paidEvent.stripeInvoiceAmount, paidEvent.stripeInvoiceCurrency)}
										</Badge>
									</OverlayTrigger>
								) : (
									<Badge variant={'success'} className={'subsync-badge'}>
										{currency(paidEvent.stripeInvoiceAmount, paidEvent.stripeInvoiceCurrency)}
									</Badge>
								)
							) : (
								<></>
							)
						},
						showHeader: true,
						headerText: 'Stripe Fee',
						sortColumnName: 'stripeInvoiceAmount',
						filterType: 'string',
						filterOptions: {
							columnName: 'stripeInvoiceAmount',
						},
					},
			  ]
			: []
	)

export { InvoiceHistory }
